import React, { useMemo, useState, useEffect } from 'react';
import { useLocation, Outlet, Navigate } from 'react-router-dom';
import { Alert, message } from 'antd';
import CartSummaryDetails from '../cartSummaryDetails';
import Shipping from '../shipping/shipping';
import ShoppingNav from '../shoppingNav';
import Spinner from '../spinner';
import { useAppSelector } from '../../hooks/redux';
import { getCartValidationIssues } from '../../utils/cartValidation';
import { useCartTotalAndTaxEstimate } from '../shoppingCartPage/useShoppingCartPageEffects';
import './checkoutPage.scss';

const CheckoutPage= () => {
    const location = useLocation();
    const { cartItems, error, status, materialPriceAndImgForCart } = useAppSelector((state) => state.shoppingCart);
    const totalAmount = useAppSelector((state) => state.shoppingCart.totalAmount);
    const taxEstimateAddress = useAppSelector((state) => state.shoppingCart.taxEstimateAddress);
    const authChecked = useAppSelector((state) => state.user.authChecked);
    const userShipping = useAppSelector((state) => state.user.shipping);
    const guestShipping = useAppSelector((state) => state.shoppingCart.guestShippingAddress);
    const userId = useAppSelector((state) => state.user.user?.id);
    const [animateOut, setAnimateOut] = useState(false);
    const [animateIn, setAnimateIn] = useState(true);
    const [checkoutWaitTimedOut, setCheckoutWaitTimedOut] = useState(false);
    const isDirectCheckoutRoute = location.pathname.endsWith('/checkout');
    const isReviewConfirmRoute = location.pathname.endsWith('/review-confirm');
    const isWaitingForCart =
        !authChecked ||
        ((status === 'idle' || status === 'loading') && cartItems.length === 0);
    const isCartEmpty = status === 'succeeded' && cartItems.length === 0;
    const hasCartLoadError = status === 'failed' && cartItems.length === 0;
    const materialDataByItemId = useMemo(
        () =>
            Object.fromEntries(
                materialPriceAndImgForCart.map((entry) => [entry.itemId, entry])
            ),
        [materialPriceAndImgForCart]
    );
    const cartValidationIssues = useMemo(
        () => getCartValidationIssues(cartItems, materialDataByItemId),
        [cartItems, materialDataByItemId]
    );
    const firstCartIssue = cartValidationIssues[0];
    const shouldRedirectInvalidCheckout =
        !isWaitingForCart &&
        !isReviewConfirmRoute &&
        (isCartEmpty || cartValidationIssues.length > 0);
    const checkoutShipping = userId ? userShipping : guestShipping;
    const taxProvince = taxEstimateAddress.province || checkoutShipping?.province || '';

    useCartTotalAndTaxEstimate({
        cartItems,
        materialPriceAndImgForCart,
        province: taxProvince,
        totalAmount,
    });

    useEffect(() => {
        if (isDirectCheckoutRoute) {
            setAnimateOut(false);
            setAnimateIn(true);
        } else {
            setAnimateOut(true);
            setAnimateIn(false);
        }
    }, [location.pathname, isDirectCheckoutRoute]);

    useEffect(() => {
        if (shouldRedirectInvalidCheckout) {
            message.warning(
                isCartEmpty
                    ? 'Please add a product to the cart before checkout.'
                    : firstCartIssue?.message || 'Please fix cart item availability before checkout.'
            );
        }
    }, [firstCartIssue?.message, isCartEmpty, shouldRedirectInvalidCheckout]);

    useEffect(() => {
        if (!isWaitingForCart) {
            setCheckoutWaitTimedOut(false);
            return;
        }

        const timeoutId = window.setTimeout(() => {
            setCheckoutWaitTimedOut(true);
        }, 12000);

        return () => window.clearTimeout(timeoutId);
    }, [isWaitingForCart]);

    if (shouldRedirectInvalidCheckout) {
        return <Navigate to="/cart" replace />;
    }

    return (
        <div className="shopping-cart-container">
            <div className="cart-header">
                <ShoppingNav />
            </div>
            <div className="shopping-cart-page">
                {isWaitingForCart && !checkoutWaitTimedOut && (
                    <div className="checkout-state">
                        <Spinner />
                    </div>
                )}
                {isWaitingForCart && checkoutWaitTimedOut && (
                    <div className="checkout-empty-state">
                        <Alert
                            message="Checkout is taking too long to load"
                            description="We could not finish loading your cart. Refresh the page or return to the cart and try checkout again."
                            type="warning"
                            showIcon
                        />
                    </div>
                )}
                {hasCartLoadError && (
                    <div className="checkout-empty-state">
                        <Alert
                            message="Checkout is unavailable"
                            description={error || 'We could not load your cart. Please refresh the page or return to the cart.'}
                            type="error"
                            showIcon
                        />
                    </div>
                )}
                {!isWaitingForCart && !hasCartLoadError && !isCartEmpty && isDirectCheckoutRoute && (
                    <>
                        <div className={`shipping-container ${animateOut ? 'slide-out' : animateIn ? 'slide-in' : ''}`}>
                            <Shipping />
                        </div>
                        <div className={`summary-container ${animateIn ? 'slide-in' : ''}`}>
                            <CartSummaryDetails />
                        </div>
                    </>
                )}
                {!isWaitingForCart && !hasCartLoadError && (!isCartEmpty || isReviewConfirmRoute) && <Outlet />}
            </div>
        </div>
    );
};

export default CheckoutPage;
